import { cn } from '@repo/ui'
import { memo } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import type { Components } from 'react-markdown'

// Templates ingested from .docx become GFM markdown (pandoc `-t gfm`), which has no
// underline syntax and so emits raw HTML like `<u>...</u>`. rehype-raw parses that HTML
// and rehype-sanitize strips anything dangerous (scripts, event handlers, unsafe URLs)
// before it renders. We keep the GitHub-flavoured default safelist and add the inline
// formatting tags pandoc produces that it omits.
//
// `img` is removed from the default safelist rather than merely left unstyled. Agent
// answers are built from retrieved documents the asker did not author, and an image
// loads without interaction, so a markdown image is an unattended egress channel for
// anything the model can be induced to put in its URL. That was reproduced end to end.
// Links are deliberately kept: they need a click, and citing sources is worth that.
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'u', 'mark'].filter((tag) => tag !== 'img')
}

const components: Components = {
  h1: ({ children }) => <h1 className="mb-3 text-xl leading-[1.2] font-semibold tracking-[-0.02em]">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-2 text-lg leading-[1.2] font-semibold tracking-[-0.02em]">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-2 text-base leading-[1.2] font-semibold tracking-[-0.02em]">{children}</h3>,
  h4: ({ children }) => <h4 className="mb-2 text-base leading-[1.2] font-semibold tracking-[-0.02em]">{children}</h4>,
  p: ({ children }) => <p className="mb-3 text-sm">{children}</p>,
  a: ({ children, href }) => (
    <a className="text-primary underline-offset-2 hover:underline" href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
  ul: ({ children }) => <ul className="mb-3 ml-6 list-disc text-sm">{children}</ul>,
  ol: ({ children }) => <ol className="mb-3 ml-6 list-decimal text-sm">{children}</ol>,
  li: ({ children }) => <li className="mb-1">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="mb-3 border-l-4 border-border pl-4 italic">{children}</blockquote>
  ),
  pre: ({ children }) => <pre className="mb-3 overflow-x-auto rounded-md bg-muted p-3 text-sm">{children}</pre>,
  code: ({ children, className }) => {
    const isBlock = className?.startsWith('language-')
    if (isBlock) {
      return <code className={cn('font-mono text-sm', className)}>{children}</code>
    }
    return <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{children}</code>
  },
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  del: ({ children }) => <del className="text-muted-foreground line-through">{children}</del>,
  hr: () => <hr className="my-4 border-border" />,
  table: ({ children }) => (
    <div className="mb-3 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="border-b border-border">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="border-b border-border last:border-0">{children}</tr>,
  th: ({ children, style }) => (
    <th className="px-3 py-2 text-left font-semibold" style={style}>
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td className="px-3 py-2 align-top" style={style}>
      {children}
    </td>
  )
}

function MarkdownRendererImpl({ children, className }: { children: string; className?: string }) {
  return (
    <div className={className}>
      <Markdown
        components={components}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
      >
        {children}
      </Markdown>
    </div>
  )
}

export const MarkdownRenderer = memo(MarkdownRendererImpl)
